<template>
    <div>
        <DTable
            id="runtimes-table"
            :fields="fields"
            :items="tableItems"
            class="border runtimes-table table-responsive"
        >
            <template #action-header>
                <DButton
                    id="runtime-btn-add"
                    class="float-end"
                    color="primary"
                    icon="plus"
                    :mini="true"
                    :disabled="rowAddDisabled"
                    @click="showAddRuntimeModal"
                />
            </template>
            <template #start_date="{item}">
                <td v-if="item.start_date">
                    {{ item.start_date }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #contract_term="{item}">
                <td
                    v-if="!item.contract_term || !item.contract_term_unit || !item.notice_period || !item.notice_period_unit"
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
                <td v-else>
                    {{ `${item.contract_term} ${__(`customers.contracts.units.${item.contract_term_unit.toLowerCase()}`)}` }} / {{ `${item.notice_period} ${__(`customers.contracts.units.${item.notice_period_unit.toLowerCase()}`)}` }}
                </td>
            </template>
            <template #action="{item}">
                <td class="text-right">
                    <DButton
                        v-if="rowEditDisabled === false"
                        :id="`runtime-btn-edit-${item.index}`"
                        icon="pencil"
                        :mini="true"
                        @click="showEditRuntimeModal(item.index)"
                    />
                    <DButton
                        :id="`runtime-btn-delete-${item.index}`"
                        color="danger"
                        icon="trash-can-outline"
                        :mini="true"
                        :disabled="rowDeletionDisabled"
                        @click="removeRuntime(item.index)"
                    />
                </td>
            </template>
        </DTable>
        <RuntimeModal
            v-if="runtimeModalVisible"
            :show-modal="runtimeModalVisible"
            :errors-data="modalErrors"
            :runtime="modalRuntime"
            :contract-term-units="contractTermUnits"
            :disable-start-date-change="disableStartDateChange"
            :index="runtimeIndex"
            @save-runtime="saveRuntime"
            @close-modal="closeModal"
        />
    </div>
</template>

<script>
import RuntimeModal from '~@/Components/Customers/Contracts/Modals/RuntimeModal.vue'

const getNewRuntimeBase = () => {
    return {
        start_date: null,
        contract_term: null,
        contract_term_unit: null,
        notice_period: null,
        notice_period_unit: null
    }
}

export default {
    name: 'Runtimes',
    components: { RuntimeModal },
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        form: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        contractTermUnits: {
            type: Array,
            default: () => { return [] }
        },
        isPreLiveContract: {
            type: Boolean,
            default: false
        }
    },
    emits: ['updateRuntime', 'addRuntime', 'removeRuntime'],
    data () {
        return {
            fields: [
                { key: 'start_date', label: this.__('customers.contracts.start_date') },
                { key: 'contract_term', label: this.__('customers.contracts.contract_term') },
                { key: 'action', label: '', _classes: 'text-right' }
            ],
            runtimeModalVisible: false,
            runtimeIndex: null,
            modalRuntime: {},
            modalErrors: {},
            disableStartDateChange: false,
            hideErrors: false
        }
    },
    computed: {
        tableItems () {
            return this.items.map((item, index) => ({ index, ...item }))
        },
        rowAddDisabled () {
            return this.isPreLiveContract === false
        },
        rowEditDisabled () {
            return this.isPreLiveContract === false
        },
        rowDeletionDisabled () {
            return this.isPreLiveContract === false || Object.values(this.form.runtimes).length === 1
        },
        items () {
            const items = []

            for (const runtimeItemIndex in this.form.runtimes) {
                if (this.rowHasErrors(this.errors, runtimeItemIndex) && this.hideErrors === false) {
                    items.push({ ...this.form.runtimes[runtimeItemIndex], _classes: 'row-has-errors' })
                } else {
                    items.push(this.form.runtimes[runtimeItemIndex])
                }
            }

            return items
        }
    },
    watch: {
        errors () {
            this.hideErrors = false
        }
    },
    methods: {
        showAddRuntimeModal () {
            this.modalRuntime = getNewRuntimeBase()
            this.runtimeIndex = null

            this.setDisableStartDateChange()
            this.setModalErrors()

            this.runtimeModalVisible = true
        },
        showEditRuntimeModal (index) {
            if (this.rowEditDisabled) {
                return
            }

            this.modalRuntime = this.form.runtimes[index]
            this.runtimeIndex = index

            this.setDisableStartDateChange()
            this.setModalErrors()

            this.runtimeModalVisible = true
        },
        setDisableStartDateChange () {
            this.disableStartDateChange = false

            if (this.runtimeIndex === 0) {
                this.modalRuntime.start_date = this.form.start_date
                this.disableStartDateChange = true
            }
        },
        setModalErrors () {
            if (this.runtimeIndex === null) {
                this.modalErrors = {}

                return
            }

            this.modalErrors = this.getErrorsForRow(this.errors, this.runtimeIndex)
        },
        rowHasErrors (errors, index) {
            return Object.values(this.getErrorsForRow(errors, index)).length > 0
        },
        getErrorsForRow (errors, index) {
            const rowErrors = {}

            if (typeof errors[`runtimes.${index}.start_date`] !== 'undefined') {
                rowErrors.start_date = errors[`runtimes.${index}.start_date`]
            }

            if (typeof errors[`runtimes.${index}.contract_term`] !== 'undefined') {
                rowErrors.contract_term = errors[`runtimes.${index}.contract_term`]
            }

            if (typeof errors[`runtimes.${index}.contract_term_unit`] !== 'undefined') {
                rowErrors.contract_term_unit = errors[`runtimes.${index}.contract_term_unit`]
            }

            if (typeof errors[`runtimes.${index}.notice_period`] !== 'undefined') {
                rowErrors.notice_period = errors[`runtimes.${index}.notice_period`]
            }

            if (typeof errors[`runtimes.${index}.notice_period_unit`] !== 'undefined') {
                rowErrors.notice_period_unit = errors[`runtimes.${index}.notice_period_unit`]
            }

            return rowErrors
        },
        saveRuntime (runtimeData) {
            this.runtimeIndex !== null
                ? this.$emit('updateRuntime', this.runtimeIndex, runtimeData)
                : this.$emit('addRuntime', runtimeData)
        },
        removeRuntime (index) {
            this.$emit('removeRuntime', index)
            this.hideErrors = true
        },
        closeModal () {
            this.runtimeModalVisible = false
        }
    }
}
</script>

<style lang="scss">
.runtimes-table {
    .invalid-value {
        color: var(--colorDanger);
        font-weight: bold;
    }
    td {
        vertical-align: middle;
    }
    table {
        margin-bottom: 0 !important;
    }
}
</style>
